import React from "react";
import { Box, Button, Flex, HStack, Icon, Text, useToast, VStack, Wrap, WrapItem } from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { motion } from "framer-motion";
import {
  PiEyeSlashDuotone, PiGlobeDuotone, PiLockDuotone, PiUsersThreeDuotone,
  PiIdentificationCardDuotone, PiSealCheckDuotone, PiFingerprintDuotone, PiPercentDuotone, PiListChecksDuotone,
  PiUsersFourDuotone, PiFileTextDuotone, PiUserPlusDuotone, PiPlayCircleDuotone, PiFlagCheckeredDuotone, PiHourglassMediumDuotone,
  PiToggleLeftDuotone, PiRadioButtonDuotone, PiCheckSquareDuotone, PiScalesDuotone, PiCheckCircleDuotone,
  PiCheckBold, PiGearDuotone, PiCopyDuotone, PiTextAaDuotone, PiHashDuotone, PiQrCodeDuotone
} from "react-icons/pi";
import { QRCodeSVG } from "qrcode.react";
import { getDateObject, VotingQuestionArrayType, QuestionType } from "@lubbezposrednio-frontend/core";
import { CreateVotingViewModel } from "../CreateVotingView/CreateVotingViewModel";
import { GLASS, drgba } from "../../components/glassUi";

const ELECTORATE_LABEL: Record<string, string> = { closed: "Zamknięta lista", signup: "Zapisy (vox populi)", open: "Otwarte referendum" };

const EASE_ARR = [0.22, 1, 0.36, 1] as const;
const MIN = 60 * 1000, HOUR = 60 * MIN, DAY = 24 * HOUR;
const isComplete = (rec: Record<string, string>) => Object.values(rec).every(v => v !== "" && v != null);
const fmtLong = (d: Date) => {
  try { return new Intl.DateTimeFormat("pl-PL", { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(d); }
  catch { return d.toISOString(); }
};
const fmtDur = (ms: number) => {
  const days = Math.floor(ms / DAY), hours = Math.floor((ms % DAY) / HOUR), mins = Math.floor((ms % HOUR) / MIN);
  const p: string[] = [];
  if (days) p.push(days === 1 ? "1 dzień" : `${days} dni`);
  if (hours) p.push(hours === 1 ? "1 godz." : `${hours} godz.`);
  if (mins) p.push(`${mins} min`);
  return p.join(" ") || "0 min";
};

const Q_TYPE: Record<QuestionType, { label: string; icon: React.ElementType; color: string }> = {
  yes_no: { label: "Tak / Nie", icon: PiToggleLeftDuotone, color: "#2563eb" },
  single: { label: "Jeden wybór", icon: PiRadioButtonDuotone, color: "#7b2ff7" },
  multiple: { label: "Wiele wyborów", icon: PiCheckSquareDuotone, color: "#10b981" }
};
const thresholdSummary = (q: VotingQuestionArrayType): string => {
  const t = q.thresholdType ?? "plurality";
  if (t === "two_thirds") return "Większość 2/3";
  if (t === "custom") return `Próg ${q.thresholdPercent ?? "—"}%`;
  if (t === "plurality" && q.qtype === "multiple") return "Ranking";
  return "Zwykła większość";
};

// ── Pojedynczy fakt: ikona + etykieta + wartość + zdanie tłumaczące ──
const Fact: React.FC<{ icon: React.ElementType; color: string; label: string; value: string; desc: string }> = ({ icon, color, label, value, desc }) => (
  <Flex gap="13px" py="13px" align="flex-start">
    <Flex w="40px" h="40px" borderRadius="12px" bg={drgba(color, 0.12)} align="center" justify="center" flexShrink={0}>
      <Icon as={icon} boxSize="21px" color={color} />
    </Flex>
    <Box flex="1" minW={0}>
      <Text fontSize="11px" fontWeight="900" letterSpacing="0.05em" textTransform="uppercase" color="#a29bb2">{label}</Text>
      <Text fontSize="15px" fontWeight="800" color="#241536" lineHeight="1.25" mt="1px">{value}</Text>
      <Text fontSize="13px" color="#8a8397" mt="3px" lineHeight="1.5">{desc}</Text>
    </Box>
  </Flex>
);

const Group: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box bg="rgba(255,255,255,0.55)" border="1px solid rgba(24,12,48,0.06)" borderRadius="18px" p={{ base: "16px", md: "20px" }}>
    <Text fontSize="12px" fontWeight="900" letterSpacing="0.05em" textTransform="uppercase" color="#7b2ff7" mb="4px">{title}</Text>
    <VStack align="stretch" spacing="0" divider={<Box h="1px" bg="rgba(24,12,48,0.05)" />}>{children}</VStack>
  </Box>
);

export const StepSummary: React.FC<{ model: CreateVotingViewModel; stepper?: React.ReactNode }> = observer(({ model, stepper }) => {
  const toast = useToast();

  // ── Ekran sukcesu (po publikacji) — nowy design ──
  if (model.confirmData.isVisible) {
    const { votingId, startDate } = model.confirmData;
    const copyLink = () => {
      if (!votingId) return;
      navigator.clipboard?.writeText(`${window.location.origin}/glosowanie/${votingId}`);
      toast({ title: "Skopiowano link do głosowania", status: "success", duration: 1800, isClosable: true });
    };
    const link = votingId ? `${window.location.origin}/glosowanie/${votingId}` : "";
    const cd = model.confirmData;
    const dataRows: { icon: React.ElementType; color: string; label: string; value: string }[] = [
      { icon: PiTextAaDuotone, color: "#2563eb", label: "Nazwa", value: cd.title || "—" },
      { icon: PiPlayCircleDuotone, color: "#10b981", label: "Start", value: cd.startDate || "—" },
      { icon: PiFlagCheckeredDuotone, color: "#e51f44", label: "Koniec", value: cd.endDate || "—" },
      { icon: PiUsersFourDuotone, color: "#2563eb", label: "Kto głosuje", value: ELECTORATE_LABEL[cd.electorateType || "closed"] + (cd.electorateType === "closed" ? ` · ~${cd.votersCount ?? 0} os.` : "") },
      { icon: cd.isPublic ? PiGlobeDuotone : PiEyeSlashDuotone, color: cd.isPublic ? "#2563eb" : "#6b7280", label: "Widoczność", value: cd.isPublic ? "Publiczne" : "Niepubliczne" },
      { icon: cd.isSecret ? PiLockDuotone : PiUsersThreeDuotone, color: cd.isSecret ? "#7b2ff7" : "#f59e0b", label: "Tajność", value: cd.isSecret ? "Tajne" : "Jawne imienne" },
      { icon: PiListChecksDuotone, color: "#0d9488", label: "Pytania", value: String(cd.questionsCount ?? 0) },
      { icon: PiPercentDuotone, color: "#f59e0b", label: "Kworum", value: cd.quorumPercent != null ? `${cd.quorumPercent}%` : "Brak" },
      ...(votingId ? [{ icon: PiHashDuotone, color: "#8a8397", label: "ID", value: votingId }] : [])
    ];
    return (
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE_ARR }}>
        <Box borderRadius="24px" p={{ base: "30px 20px", md: "44px 40px" }} position="relative" overflow="hidden" sx={GLASS}>
          <Box position="absolute" top="-100px" left="50%" transform="translateX(-50%)" w="420px" h="420px" borderRadius="full" pointerEvents="none" sx={{ background: `radial-gradient(circle, ${drgba("#10b981", 0.16)}, transparent 70%)` }} />
          <Flex direction="column" align="center" textAlign="center" gap="18px" position="relative">
            <motion.div initial={{ scale: 0, rotate: -60 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 340, damping: 16 }}>
              <Flex w="80px" h="80px" borderRadius="full" align="center" justify="center" style={{ background: "linear-gradient(140deg,#10b981,#34d9a4)" }} boxShadow="0 20px 44px -16px rgba(16,185,129,0.7)">
                <Icon as={PiCheckBold} boxSize="38px" color="white" />
              </Flex>
            </motion.div>
            <Box>
              <Text fontSize={{ base: "24px", md: "30px" }} fontWeight="900" color="#0f0820" letterSpacing="-0.03em">Głosowanie utworzone!</Text>
              <Text fontSize="14px" color="#6b6480" mt="6px" maxW="440px" mx="auto" lineHeight="1.55">
                {startDate ? `Będzie widoczne od ${startDate}. Zaproszenia wyjdą w dniu startu.` : "Zaproszenia wyjdą w dniu startu głosowania."}
              </Text>
            </Box>

            {/* QR + link + pełne dane */}
            <Flex direction={{ base: "column", md: "row" }} gap="14px" w="100%" maxW="700px" mt="4px" textAlign="left">
              {votingId && (
                <VStack spacing="12px" bg="rgba(255,255,255,0.6)" border="1px solid rgba(24,12,48,0.06)" borderRadius="18px" p="18px" flexShrink={0} align="stretch" w={{ base: "100%", md: "230px" }}>
                  <HStack spacing="8px" alignSelf="flex-start">
                    <Icon as={PiQrCodeDuotone} boxSize="17px" color="#7b2ff7" />
                    <Text fontSize="11.5px" fontWeight="900" letterSpacing="0.05em" textTransform="uppercase" color="#7b2ff7">Link do głosowania</Text>
                  </HStack>
                  <Flex justify="center"><Box p="10px" bg="white" borderRadius="14px" boxShadow="0 10px 30px -16px rgba(43,0,90,0.4)"><QRCodeSVG value={link} size={148} fgColor="#1b1030" bgColor="#ffffff" level="M" /></Box></Flex>
                  <HStack spacing="6px" w="100%" bg="rgba(24,12,48,0.04)" borderRadius="10px" pl="12px" pr="5px" py="5px">
                    <Text flex="1" fontSize="12px" fontWeight="600" color="#4a4458" noOfLines={1}>{link}</Text>
                    <Button size="xs" h="30px" onClick={copyLink} leftIcon={<Icon as={PiCopyDuotone} boxSize="13px" />} bg="primary" color="white" borderRadius="8px" fontWeight="800" fontSize="12px" _hover={{ bg: "#36007a" }} flexShrink={0}>Kopiuj</Button>
                  </HStack>
                </VStack>
              )}

              <Box flex="1" bg="rgba(255,255,255,0.6)" border="1px solid rgba(24,12,48,0.06)" borderRadius="18px" p="18px" minW={0}>
                <HStack spacing="8px" mb="12px">
                  <Icon as={PiListChecksDuotone} boxSize="17px" color="#0d9488" />
                  <Text fontSize="11.5px" fontWeight="900" letterSpacing="0.05em" textTransform="uppercase" color="#0d9488">Szczegóły głosowania</Text>
                </HStack>
                <VStack align="stretch" spacing="7px">
                  {dataRows.map(r => (
                    <Flex key={r.label} align="center" gap="11px">
                      <Flex w="34px" h="34px" borderRadius="10px" bg={drgba(r.color, 0.12)} align="center" justify="center" flexShrink={0}>
                        <Icon as={r.icon} boxSize="17px" color={r.color} />
                      </Flex>
                      <Text fontSize="12.5px" color="#8a8397" fontWeight="600" flexShrink={0}>{r.label}</Text>
                      <Text flex="1" fontSize="13px" fontWeight="800" color="#241536" textAlign="right" noOfLines={1} title={r.value}>{r.value}</Text>
                    </Flex>
                  ))}
                </VStack>
              </Box>
            </Flex>

            <Wrap justify="center" spacing="10px" pt="6px">
              <WrapItem>
                <Button as={Link} to={votingId ? `/organizator/glosowanie/${votingId}` : "/organizator/twoje-glosowania"} leftIcon={<Icon as={PiGearDuotone} boxSize="17px" />} bg="primary" color="white" borderRadius="13px" h="50px" px="22px" fontWeight="800" _hover={{ bg: "#36007a" }}>Zarządzaj głosowaniem</Button>
              </WrapItem>
              <WrapItem>
                <Button as={Link} to="/organizator/twoje-glosowania" leftIcon={<Icon as={PiListChecksDuotone} boxSize="17px" />} variant="ghost" color="#6b6480" borderRadius="13px" h="50px" px="18px" fontWeight="700" _hover={{ bg: "rgba(24,12,48,0.05)", color: "primary" }}>Twoje głosowania</Button>
              </WrapItem>
            </Wrap>
          </Flex>
        </Box>
      </motion.div>
    );
  }

  const d = model.votingDataModel;
  const startDate = isComplete(d.startVotingDate) ? getDateObject(d.startVotingDate) : null;
  const endDate = isComplete(d.endVotingDate) ? getDateObject(d.endVotingDate) : null;
  const durationMs = startDate && endDate ? Math.max(0, endDate.getTime() - startDate.getTime()) : null;

  const questions = model.votingQuestionsModel.questionsArray.filter(q => q.question?.trim());
  const ev = model.eligibleVotersModel;
  const hasIdentity = model.accountRequired || model.requiresVerification;
  const isAppOnly = model.voteConfirmation === "app";
  const hasDocs = !!(model.customRegulations || model.customRegulationsUrl || model.customPrivacyPolicy || model.customPrivacyPolicyUrl);

  // ── Widoczność / tajność ──
  const visFact = model.isPublic
    ? { icon: PiGlobeDuotone, color: "#2563eb", value: "Publiczne", desc: "Temat, pytania i wyniki zobaczy każdy — także osoby spoza listy uprawnionych." }
    : { icon: PiEyeSlashDuotone, color: "#6b7280", value: "Niepubliczne", desc: "Temat i wyniki zobaczą tylko osoby uprawnione do głosowania." };
  const secFact = model.isSecret
    ? { icon: PiLockDuotone, color: "#7b2ff7", value: "Tajne", desc: "Nikt — nawet organizator — nie zobaczy, jak zagłosowała konkretna osoba (dowód ZK)." }
    : { icon: PiUsersThreeDuotone, color: "#f59e0b", value: "Jawne imienne", desc: "Przy każdym głosie widać, kto i jak zagłosował — jak w jawnym głosowaniu rady." };

  // ── Tożsamość ──
  const idFact = model.requiresVerification
    ? { value: "Zweryfikowana tożsamość", desc: "Wymagane konto z potwierdzoną tożsamością (mObywatel) — odporność na fałszywe konta." }
    : model.accountRequired
    ? { value: "Wymagane konto", desc: "Głosować mogą tylko zalogowani użytkownicy platformy." }
    : { value: "Bez konta", desc: "Wystarczy zaproszenie (token / e-mail) — najniższy próg wejścia." };

  // ── Potwierdzanie ──
  const confFact = { in_session: { value: "W sesji", desc: "Zalogowany potwierdza od razu w panelu; osoby bez konta — mailem." },
    email: { value: "Mail / apka", desc: "Push w aplikacji, a gdy nie ma sesji — kaskada na e-mail." },
    app: { value: "Tylko aplikacja", desc: "Wyłącznie push w aplikacji, bez kaskady — najwyższy poziom pewności." } }[model.voteConfirmation] ?? { value: model.voteConfirmation, desc: "" };

  // ── Podpis ──
  const walFact = { none: { value: "Brak", desc: "Potwierdzenie tokenem/loginem wystarczy." },
    recommended: { value: "Zalecany", desc: "Aplikacja zachęca do podpisu kluczem tożsamości; bez podpisu też przejdzie." },
    required: { value: "Wymagany", desc: "Bez podpisu cyfrową tożsamością nie da się zatwierdzić głosu." } }[model.walletPolicy] ?? { value: model.walletPolicy, desc: "" };

  // ── Kworum ──
  const quorumFact = model.quorumPercent != null
    ? { value: `${model.quorumPercent}%`, desc: `Głosowanie jest ważne, jeśli zagłosuje co najmniej ${model.quorumPercent}% uprawnionych.` }
    : { value: "Brak", desc: "Głosowanie jest ważne niezależnie od frekwencji." };

  // ── Elektorat ──
  const signupClose = model.signupClosesAt ? new Date(model.signupClosesAt) : null;
  const elFact = model.electorateType === "closed"
    ? { icon: PiUsersFourDuotone, color: "#2563eb", value: `Zamknięta lista · ~${ev.totalCount} os.`, desc: "Głosują wyłącznie osoby z Twojej listy (grupy / konta / CSV)." }
    : model.electorateType === "signup"
    ? { icon: PiUserPlusDuotone, color: "#7b2ff7", value: "Zapisy (vox populi)", desc: signupClose ? `Każdy zapisze się przez publiczny link; zapisy zamykają się ${fmtLong(signupClose)}, potem lista staje się elektoratem.` : "Każdy zapisze się przez publiczny link; po zamknięciu zapisów lista staje się elektoratem." }
    : { icon: PiGlobeDuotone, color: "#10b981", value: "Otwarte referendum", desc: "Głosuje każdy zweryfikowany przez mObywatel — bez listy uprawnionych." };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE_ARR }}>
      <Box borderRadius="24px" p={{ base: "24px", md: "34px" }} position="relative" overflow="hidden" sx={GLASS}>
        <Box position="absolute" top="-90px" right="-70px" w="320px" h="320px" borderRadius="full" pointerEvents="none" sx={{ background: `radial-gradient(circle, ${drgba("#10b981", 0.09)}, transparent 70%)` }} />

        {stepper && <Box position="relative" mb={{ base: "20px", md: "26px" }}>{stepper}</Box>}

        {/* Hero: tytuł + opis + oś terminu */}
        <Box position="relative" mb="20px">
          <HStack spacing="8px" mb="8px">
            <Icon as={PiCheckCircleDuotone} boxSize="16px" color="#10b981" />
            <Text fontSize="12px" fontWeight="900" letterSpacing="0.05em" textTransform="uppercase" color="#10b981">Podsumowanie — sprawdź i opublikuj</Text>
          </HStack>
          <Text fontSize={{ base: "26px", md: "34px" }} fontWeight="900" color="#0f0820" letterSpacing="-0.03em" lineHeight="1.1">{d.name || "Bez nazwy"}</Text>
          {d.description?.trim() && <Text fontSize="14px" color="#6b6480" mt="8px" lineHeight="1.55">{d.description}</Text>}
        </Box>

        {/* Oś terminu */}
        {startDate && endDate && (
          <Box position="relative" mb="16px" bg="rgba(255,255,255,0.55)" border="1px solid rgba(24,12,48,0.06)" borderRadius="18px" p={{ base: "16px", md: "18px" }}>
            <Flex align="center" gap="12px">
              <Flex w="36px" h="36px" borderRadius="11px" bg={drgba("#10b981", 0.12)} align="center" justify="center" flexShrink={0}><Icon as={PiPlayCircleDuotone} boxSize="19px" color="#10b981" /></Flex>
              <Box flex="1" position="relative" h="6px"><Box position="absolute" inset="0" borderRadius="full" bg={`linear-gradient(90deg,#10b981,#7b2ff7,#e51f44)`} opacity={0.85} /></Box>
              <HStack spacing="6px" px="12px" py="5px" borderRadius="full" bg={drgba("#7b2ff7", 0.1)} flexShrink={0}><Icon as={PiHourglassMediumDuotone} boxSize="14px" color="#7b2ff7" /><Text fontSize="12.5px" fontWeight="800" color="#7b2ff7" whiteSpace="nowrap">{fmtDur(durationMs ?? 0)}</Text></HStack>
              <Box flex="1" position="relative" h="6px"><Box position="absolute" inset="0" borderRadius="full" bg={`linear-gradient(90deg,#7b2ff7,#e51f44)`} opacity={0.85} /></Box>
              <Flex w="36px" h="36px" borderRadius="11px" bg={drgba("#e51f44", 0.12)} align="center" justify="center" flexShrink={0}><Icon as={PiFlagCheckeredDuotone} boxSize="19px" color="#e51f44" /></Flex>
            </Flex>
            <Flex justify="space-between" mt="10px" gap="10px">
              <Box><Text fontSize="10.5px" fontWeight="900" letterSpacing="0.05em" textTransform="uppercase" color="#10b981">Start</Text><Text fontSize="13px" fontWeight="700" color="#241536" textTransform="capitalize">{fmtLong(startDate)}</Text></Box>
              <Box textAlign="right"><Text fontSize="10.5px" fontWeight="900" letterSpacing="0.05em" textTransform="uppercase" color="#e51f44">Koniec</Text><Text fontSize="13px" fontWeight="700" color="#241536" textTransform="capitalize">{fmtLong(endDate)}</Text></Box>
            </Flex>
          </Box>
        )}

        <VStack align="stretch" spacing="12px" position="relative">
          {/* Kto i jak głosuje */}
          <Group title="Kto może głosować i jak">
            <Fact icon={elFact.icon} color={elFact.color} label="Elektorat" value={elFact.value} desc={elFact.desc} />
            <Fact icon={PiIdentificationCardDuotone} color="#10b981" label="Wymagana tożsamość" value={idFact.value} desc={idFact.desc} />
            {hasIdentity && <Fact icon={PiSealCheckDuotone} color="#0d9488" label="Potwierdzanie głosu" value={confFact.value} desc={confFact.desc} />}
            {hasIdentity && isAppOnly && <Fact icon={PiFingerprintDuotone} color="#e51f44" label="Podpis tożsamością" value={walFact.value} desc={walFact.desc} />}
          </Group>

          {/* Widoczność i ważność */}
          <Group title="Widoczność i ważność">
            <Fact icon={visFact.icon} color={visFact.color} label="Widoczność" value={visFact.value} desc={visFact.desc} />
            <Fact icon={secFact.icon} color={secFact.color} label="Tajność głosu" value={secFact.value} desc={secFact.desc} />
            <Fact icon={PiPercentDuotone} color="#f59e0b" label="Kworum" value={quorumFact.value} desc={quorumFact.desc} />
            <Fact icon={PiFileTextDuotone} color="#430098" label="Dokumenty do akceptacji" value={hasDocs ? "Własne" : "Dokumenty platformy"} desc={hasDocs ? "Uczestnicy zaakceptują wgrany regulamin / politykę prywatności." : "Użyjemy standardowego regulaminu i polityki platformy."} />
          </Group>

          {/* Pytania */}
          <Box bg="rgba(255,255,255,0.55)" border="1px solid rgba(24,12,48,0.06)" borderRadius="18px" p={{ base: "16px", md: "20px" }}>
            <HStack spacing="8px" mb="12px">
              <Icon as={PiListChecksDuotone} boxSize="17px" color="#0d9488" />
              <Text fontSize="12px" fontWeight="900" letterSpacing="0.05em" textTransform="uppercase" color="#0d9488">Pytania</Text>
              <Box px="8px" py="1px" borderRadius="full" bg={drgba("#0d9488", 0.12)}><Text fontSize="11px" fontWeight="800" color="#0d9488">{questions.length}</Text></Box>
            </HStack>
            <VStack align="stretch" spacing="10px">
              {questions.map((q, i) => {
                const meta = Q_TYPE[q.qtype ?? "single"];
                const answers = q.qtype === "yes_no" ? ["Tak", "Nie"] : q.answers.filter(a => a.content.trim()).map(a => a.content);
                return (
                  <Box key={q.id} bg="rgba(24,12,48,0.028)" borderRadius="14px" p="13px 15px">
                    <Flex align="flex-start" gap="10px">
                      <Flex w="24px" h="24px" borderRadius="8px" bg={meta.color} color="white" align="center" justify="center" fontWeight="800" fontSize="12px" flexShrink={0}>{i + 1}</Flex>
                      <Box flex="1" minW={0}>
                        <Text fontSize="14px" fontWeight="800" color="#241536" lineHeight="1.3">{q.question}</Text>
                        <Wrap spacing="6px" mt="8px">
                          <WrapItem><HStack spacing="5px" px="8px" py="3px" borderRadius="full" bg={drgba(meta.color, 0.1)}><Icon as={meta.icon} boxSize="12px" color={meta.color} /><Text fontSize="11.5px" fontWeight="700" color={meta.color}>{meta.label}</Text></HStack></WrapItem>
                          <WrapItem><HStack spacing="5px" px="8px" py="3px" borderRadius="full" bg="rgba(24,12,48,0.05)"><Icon as={PiScalesDuotone} boxSize="12px" color="#8a8397" /><Text fontSize="11.5px" fontWeight="700" color="#6b6480">{thresholdSummary(q)}</Text></HStack></WrapItem>
                          {q.allowAbstain && <WrapItem><HStack spacing="5px" px="8px" py="3px" borderRadius="full" bg="rgba(24,12,48,0.05)"><Text fontSize="11.5px" fontWeight="700" color="#6b6480">+ wstrzymanie</Text></HStack></WrapItem>}
                        </Wrap>
                        {answers.length > 0 && (
                          <Wrap spacing="6px" mt="8px">
                            {answers.map((a, ai) => (
                              <WrapItem key={ai}><Box px="9px" py="3px" borderRadius="8px" bg="rgba(255,255,255,0.7)" border="1px solid rgba(24,12,48,0.08)"><Text fontSize="12px" fontWeight="600" color="#4a4458">{a}</Text></Box></WrapItem>
                            ))}
                          </Wrap>
                        )}
                      </Box>
                    </Flex>
                  </Box>
                );
              })}
            </VStack>
          </Box>
        </VStack>
      </Box>
    </motion.div>
  );
});
